/* eslint-disable react/no-array-index-key */
/* eslint-disable consistent-return */

import { useEffect, useRef, useState } from 'react'
import { Popover } from '@mui/material'
import MuiButton from '@mui/material/Button'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import IconButton from '@/shared/components/IconButton/IconButton'
import Button from '@/shared/components/Button/Button'
import TextField from '@/shared/components/TextField/TextField'
import AnimatedMarkdown from '@/shared/utils/AnimatedMarkdown'
import MessageLoader from '@/shared/components/MessageLoader/MessageLoader'
import styles from './ChatBot.module.scss'
import { chatBotButtonStyles, chatBotPopoverStyles } from './ChatBot.styles'
import { useChatBotStore } from './store/useChatBotStore'
import { useChatBotQuery } from './hooks/useChatBotQuery'
import { useSnackbar } from '../Snackbar/SnackbarProvider'

const GREETING_DISMISSED_KEY = 'chatbot-greeting-dismissed'

function Chatbot() {
  const [open, setOpen] = useState(false)
  // One-off greeting bubble; dismissed for the session once closed or the chat
  // opens (stored in sessionStorage, so it shows again in a new session).
  const [showGreeting, setShowGreeting] = useState(
    () => !sessionStorage.getItem(GREETING_DISMISSED_KEY)
  )
  const dismissGreeting = () => {
    setShowGreeting(false)
    sessionStorage.setItem(GREETING_DISMISSED_KEY, '1')
  }
  const [userMessage, setUserMessage] = useState('')
  const { messages, addMessage, clearMessages } = useChatBotStore()
  const { postMessage, isPending } = useChatBotQuery()
  const [isScrollable, setIsScrollable] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { t, i18n } = useTranslation()
  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkScroll = () => {
      setIsScrollable(container.scrollHeight > container.clientHeight)
    }

    checkScroll()

    const observer = new ResizeObserver(() => checkScroll())
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [messages])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkScroll = () => {
      setIsScrollable(container.scrollHeight > container.clientHeight)
    }

    checkScroll()

    const observer = new ResizeObserver(() => checkScroll())
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [])

  const buttonRef = useRef(null)

  const sendMessage = (message: string) => {
    if (message) {
      setUserMessage('')
      addMessage({ from: 'user', text: message })

      postMessage(
        {
          message,
          language: i18n.resolvedLanguage || i18n.language || 'pl',
          history: messages.slice(-6).map(item => ({
            role:
              item.from === 'user'
                ? ('user' as const)
                : ('assistant' as const),
            content: item.text.slice(-2000)
          }))
        },
        {
          onSuccess: response => {
            addMessage({
              from: 'bot',
              text: response,
              shouldAnimate: true
            })
          },
          onError: () =>
            showSnackbar(
              t('ALERTS.SEND_CHATBOT_MESSAGE_ERROR.TITLE'),
              t('ALERTS.SEND_CHATBOT_MESSAGE_ERROR.DESCRIPTION'),
              'error'
            )
        }
      )
    } else {
      setShowAlert(true)
    }
  }

  useEffect(() => {
    const lastIndex = messages.length - 1
    const last = messages[lastIndex]
    if (last?.from !== 'bot' || !last.shouldAnimate) return

    const CHAR_MS = 30
    const EXTRA = 400
    const time = last.text.length * CHAR_MS + EXTRA

    const id = setTimeout(() => {
      useChatBotStore.setState(state => {
        const copy = [...state.messages]
        if (copy[lastIndex]) copy[lastIndex].shouldAnimate = false
        return { messages: copy }
      })
    }, time)

    return () => clearTimeout(id)
  }, [messages])

  useEffect(() => {
    if (userMessage) {
      setShowAlert(false)
    }
  }, [userMessage])

  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' }) // or 'smooth'
  }, [messages, isPending])

  return (
    <div
      id="tour-chatbot"
      className={styles.chatBotIconWrapper}
      ref={buttonRef}
    >
      {!open && showGreeting && (
        <div className={styles.greetingBubble}>
          <div className={styles.greetingClose}>
            <IconButton
              icon="icon-small la-times"
              onClick={dismissGreeting}
              color="primary"
            />
          </div>
          <div className={styles.greetingContent}>
            <i
              className={`las la-comment-dots icon-medium ${styles.greetingIcon}`}
            />
            <p className={`body-medium ${styles.greetingText}`}>
              {t('CHATBOT.GREETING_BUBBLE')}
            </p>
          </div>
        </div>
      )}

      <MuiButton
        onClick={() => {
          if (!open) dismissGreeting()
          setOpen(!open)
        }}
        sx={chatBotButtonStyles}
        disableRipple
      />

      <Popover
        sx={chatBotPopoverStyles}
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transitionDuration={{ enter: 200, exit: 200 }}
      >
        <div className={styles.chatBotHeader}>
          <div className={styles.chatBotLogoContainer}>
            <p className={`${styles.chatBotLogo} body-large-logo`}>
              MENU<mark className="mark-orange">!</mark> Bot
            </p>
          </div>
          <div className={styles.chatBotHeaderButtonContainer}>
            <IconButton
              icon="icon-medium las la-sync"
              onClick={() => clearMessages()}
              color="primary"
              disabled={!messages.length}
            />
            <IconButton
              icon="icon-medium la-times"
              onClick={() => setOpen(false)}
              color="primary"
            />
          </div>
        </div>

        {!messages.length ? (
          <div className={styles.chatBotInitialContainer}>
            <div className={styles.chatBotInitialInfoContainer}>
              <p className={`body-medium ${styles.initialMessage}`}>
                {t('CHATBOT.WELCOME_MESSAGE')}
              </p>
              <p className={`body-medium ${styles.initialMessage}`}>
                {t('CHATBOT.USAGE_LIMIT')}
              </p>
            </div>
            <div className={styles.chatBotInitialQuestionsContainer}>
              <Button
                label={t('CHATBOT.QUICK.HOW_TO_ADD_PRODUCT')}
                variant="outlined"
                color="primary"
                onClick={() =>
                  sendMessage(t('CHATBOT.QUICK.HOW_TO_ADD_PRODUCT'))
                }
              />
              <Button
                label={t('CHATBOT.QUICK.DELETE_PRODUCT')}
                variant="outlined"
                color="primary"
                onClick={() => sendMessage(t('CHATBOT.QUICK.DELETE_PRODUCT'))}
              />
              <Button
                label={t('CHATBOT.QUICK.ADD_PRODUCT')}
                variant="outlined"
                color="primary"
                onClick={() => sendMessage(t('CHATBOT.QUICK.ADD_PRODUCT'))}
              />
              <Button
                label={t('CHATBOT.QUICK.ADD_EMPLOYEE')}
                variant="outlined"
                color="primary"
                onClick={() => sendMessage(t('CHATBOT.QUICK.ADD_EMPLOYEE'))}
              />
              <Button
                label={t('CHATBOT.QUICK.DELETE_EMPLOYEE')}
                variant="outlined"
                color="primary"
                onClick={() => sendMessage(t('CHATBOT.QUICK.DELETE_EMPLOYEE'))}
              />
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className={`body-medium ${styles.chatBotMessagesContainer} ${
              isScrollable ? styles.scrollable : ''
            }`}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${styles.chatBotMessage} ${
                  message.from === 'bot'
                    ? styles.botMessage
                    : styles.userMessage
                }`}
              >
                {(() => {
                  if (message.from === 'bot') {
                    if (message.shouldAnimate) {
                      return (
                        <AnimatedMarkdown
                          content={message.text}
                          speed={15}
                          scrollTargetRef={bottomRef}
                        />
                      )
                    }
                    return (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.text}
                      </ReactMarkdown>
                    )
                  }
                  return <p className="body-medium">{message.text}</p>
                })()}
              </div>
            ))}
            {isPending && <MessageLoader />}
            <div ref={bottomRef} />
          </div>
        )}

        <div className={styles.chatBotInputContainer}>
          <TextField
            multiline
            label={t('CHATBOT.INPUT_LABEL')}
            placeholder={t('CHATBOT.INPUT_PLACEHOLDER')}
            focused
            lines={3}
            onChange={e => setUserMessage(e.target.value)}
            value={userMessage}
            hasError={showAlert}
          />
        </div>

        <div className={styles.chatBotButtonsContainer}>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setUserMessage('')}
            label={t('CHATBOT.CLEAR')}
            disabled={!userMessage}
          />
          <IconButton
            icon="icon-big la-paper-plane"
            color="primary"
            onClick={() => sendMessage(userMessage)}
          />
        </div>
      </Popover>
    </div>
  )
}

export default Chatbot
